import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Sparkles, Zap, Workflow } from 'lucide-react';

const WELCOME_MODAL_KEY = 'flusia-welcome-seen-v2';

interface WelcomeModalProps {
  isOpen: boolean;
  onComplete: () => void;
}

export function WelcomeModal({ isOpen, onComplete }: WelcomeModalProps) {
  const [isClosing, setIsClosing] = useState(false);

  const handleComplete = () => {
    localStorage.setItem(WELCOME_MODAL_KEY, 'true');
    setIsClosing(true);
    setTimeout(() => {
      onComplete();
    }, 300);
  };

  if (!isOpen) return null;

  const modalRoot = document.getElementById('modal-root') || document.body;

  const features = [
    {
      icon: Sparkles,
      title: 'Design rinnovato',
      description: 'Interfaccia pulita e intuitiva',
      color: '#2563EB'
    },
    {
      icon: Zap,
      title: 'Velocità e stabilità',
      description: 'Prestazioni ottimizzate',
      color: '#059669'
    },
    {
      icon: Workflow,
      title: 'Automazioni',
      description: 'Workflow intelligenti per risparmiare tempo',
      color: '#7C3AED'
    }
  ];

  const modalContent = (
    <div
      className={`fixed inset-0 flex items-center justify-center z-[9999] transition-opacity duration-300 ${
        isClosing ? 'opacity-0' : 'opacity-100'
      }`}
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
    >
      <div
        className={`relative w-full max-w-md mx-4 bg-white rounded-xl overflow-hidden transition-all duration-300 ${
          isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
        }`}
        style={{
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)'
        }}
      >
        <div className="p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <img
              src="/icona.png"
              alt="Flusia"
              className="w-14 h-14 mx-auto mb-5 rounded-xl"
              style={{ boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}
            />
            <h1
              className="text-2xl font-semibold mb-2"
              style={{ color: '#37352F' }}
            >
              Il tuo CRM si rinnova
            </h1>
            <p
              className="text-base"
              style={{ color: '#787774' }}
            >
              Abbiamo ripensato ogni dettaglio per offrirti un'esperienza ancora migliore.
            </p>
          </div>

          {/* Features */}
          <div className="space-y-3 mb-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-start gap-4 p-3 rounded-lg transition-colors hover:bg-gray-50"
              >
                <div
                  className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${feature.color}10` }}
                >
                  <feature.icon
                    className="w-5 h-5"
                    style={{ color: feature.color }}
                    strokeWidth={1.5}
                  />
                </div>
                <div className="pt-0.5">
                  <h3
                    className="font-medium text-sm"
                    style={{ color: '#37352F' }}
                  >
                    {feature.title}
                  </h3>
                  <p
                    className="text-sm"
                    style={{ color: '#787774' }}
                  >
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <button
            onClick={handleComplete}
            className="w-full py-3 px-6 rounded-lg font-medium text-white transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
            style={{
              backgroundColor: '#2563EB',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
            }}
          >
            Iniziamo
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, modalRoot);
}

// Export helper to check if welcome modal was seen
export function hasSeenWelcomeModal(): boolean {
  return localStorage.getItem(WELCOME_MODAL_KEY) === 'true';
}

// Export helper to reset welcome modal (useful for testing)
export function resetWelcomeModal(): void {
  localStorage.removeItem(WELCOME_MODAL_KEY);
}
